import {
  backgroundSlotsCount,
  getImageRecords,
  productHasGroupRecord,
  productIsAllGroup,
  productPosesCount,
} from '../components/v3/products/imageRecords';

const createNewCartItem = (cartObject, variantId = 1) => {
  const newItemId = `${cartObject.productId}-${variantId}`;

  return {
    ...cartObject,
    id: newItemId,
    variantId: variantId,
  };
};

const createNewPersonalizationCartItem = (
  personalizationProduct,
  variantId = 1,
) => {
  const newItemId = `${personalizationProduct.productId}-${variantId}`;

  return {
    ...personalizationProduct,
    id: newItemId,
    variantId: variantId,
  };
};

const getCount = (orders) => {
  const totalCartItems = Object.values(orders).reduce((acc, order) => {
    return acc + order.cartItems.length;
  }, 0);

  return totalCartItems;
};

const getNextVariantId = (cartItems, productId) => {
  let maxVariantId = 0;

  Object.values(cartItems).forEach((cartItem) => {
    if (cartItem.productId === productId) {
      maxVariantId = Math.max(maxVariantId, cartItem.variantId);
    }
  });

  return maxVariantId + 1;
};

const marginalRetouchingPrices = ({ basePriceCents, scheduleCents, count }) => {
  const schedule = scheduleCents?.length ? scheduleCents : [basePriceCents];
  return Array.from(
    { length: count },
    (_, index) => schedule[Math.min(index, schedule.length - 1)],
  );
};

// The price of the next photo added to a retouching level in this order: the
// schedule entry at the line's current photo count (the last entry repeats),
// or the level's first price when the order has no line for it yet, plus the
// options a line charges once per photo (cartLineTotal): the line's own, or
// newLineOptionCents for a level the order has no line on yet.
const marginalRetouchingPriceForNext = ({
  product,
  cartItem,
  newLineOptionCents = 0,
}) => {
  const count = cartItem ? retouchingImageCount(cartItem) || 0 : 0;
  const scheduleCents = product?.tiered_unit_prices_cents;
  const schedule = scheduleCents?.length ? scheduleCents : [product?.price];
  const optionCents = cartItem
    ? determineCartItemPrice(cartItem) - (cartItem.price || 0)
    : newLineOptionCents;
  return schedule[Math.min(count, schedule.length - 1)] + optionCents;
};

const validRetouchingPoseIds = (poseIds) =>
  Array.isArray(poseIds) &&
  poseIds.every(
    (id) =>
      (typeof id === 'number' || typeof id === 'string') &&
      Number.isInteger(Number(id)) &&
      Number(id) > 0,
  );
const retouchingImageCount = (item) =>
  validRetouchingPoseIds(item.poseIds)
    ? new Set(item.poseIds.map(Number)).size
    : null;
const validRetouchingPrice = (product) => {
  const cents = (value) =>
    Number.isInteger(value) && value >= 0 && value <= 2147483647;
  const schedule = product?.tiered_unit_prices_cents;
  return (
    cents(product?.price) &&
    Array.isArray(schedule) &&
    schedule.every(cents) &&
    (!schedule.length || schedule[0] === product.price)
  );
};
const validRetouchingLinePrice = (item) =>
  validRetouchingPoseIds(item.poseIds) &&
  validRetouchingPrice({
    price: item.price,
    tiered_unit_prices_cents: item.retouchingTieredUnitPricesCents,
  });

// What one cart line costs.
const cartLineTotal = (cartItem) => {
  if (cartItem.retouchingPricingMode === 'per_image') {
    if (!validRetouchingLinePrice(cartItem)) return null;
    const count = retouchingImageCount(cartItem);
    const base = marginalRetouchingPrices({
      basePriceCents: cartItem.price,
      scheduleCents: cartItem.retouchingTieredUnitPricesCents,
      count,
    });
    const options = determineCartItemPrice(cartItem) - (cartItem.price || 0);
    return base.reduce((sum, cents) => sum + cents, 0) + count * options;
  }
  const unitPrice = determineCartItemPrice(cartItem);

  return unitPrice * (cartItem?.quantity || 1);
};

const calculateTotalPrice = (orders) => {
  let total = 0;

  for (let studentId in orders) {
    let studentOrder = orders[studentId];

    if (Array.isArray(studentOrder.cartItems)) {
      // Handle cartItems as array
      studentOrder.cartItems.forEach((cartItem) => {
        total += cartLineTotal(cartItem);
      });
    } else {
      // Handle cartItems as object (current structure)
      for (let cartItemId in studentOrder.cartItems) {
        total += cartLineTotal(studentOrder.cartItems[cartItemId]);
      }
    }
  }

  return total;
};

const sumUniqueBackgroundPrices = (orders) => {
  let total = 0;

  for (let studentId in orders) {
    const studentOrder = orders[studentId];
    const uniqueBackgroundIds = new Set(); // To track unique background IDs
    let singleChargeApplied = false; // Flag to track if the single charge has been applied globally

    const chargeForYpxBackgroundUpgrades =
      studentOrder.event.chargeForYpxBackgroundUpgrades;

    for (let cartItemId in studentOrder.cartItems) {
      const cartItem = studentOrder.cartItems[cartItemId];

      // Check if backgroundSelections key exists and it's an array
      if (Array.isArray(cartItem.backgroundSelections)) {
        for (const selection of cartItem.backgroundSelections) {
          let price = parseInt(selection.price, 10); // Ensure price is an integer

          // Only proceed if the price is greater than 0
          if (price > 0) {
            if (chargeForYpxBackgroundUpgrades === false && cartItem.isYPX) {
              continue;
            }

            // If single charge is enabled globally and we've already applied it, skip further additions
            if (
              selection.single_charge_for_background_upgrades &&
              singleChargeApplied
            ) {
              continue;
            }

            // If we haven't encountered this background ID before, add its price to the total
            if (!uniqueBackgroundIds.has(selection.id)) {
              total += price;
              uniqueBackgroundIds.add(selection.id); // Mark this ID as encountered

              // If single charge is enabled, set the flag to true after the first addition
              if (selection.single_charge_for_background_upgrades) {
                singleChargeApplied = true;
              }
            }
          }
        }
      }
    }
  }

  return total;
};

const allOrdersAreEmpty = (orders) => {
  return Object.values(orders).every((order) => order.cartItems.length === 0);
};

const orderHasNoRetouching = (cartItems) => {
  return (
    cartItems?.every((cartItem) => cartItem.isRetouching === false) ?? false
  );
};

const unlockedGroups = (cartItems) => {
  // Check if cartItems is an array, if not, default to an empty array
  let groups = (Array.isArray(cartItems) ? cartItems : [])
    .filter((item) => {
      return item.lockType === 'unlocker' && Array.isArray(item.lockGroup);
    })
    .flatMap((item) => item.lockGroup);

  return [...new Set(groups)];
};

// TODO: flesh this out
const getCartTotal = (orders) => {
  let total = 0;

  for (let orderId in orders) {
    total += getOrderTotal(orders[orderId].cartItems);
  }

  return total;
};

const getOrderTotal = (cartItems) => {
  const orderTotal = cartItems.reduce((total, item) => {
    // cartLineTotal includes product option prices, matching the server's
    // incentive threshold check.
    return total + cartLineTotal(item);
  }, 0);

  return orderTotal;
};

const getAllCartSKUs = (orders) => {
  const allSKUsSet = Object.values(orders).reduce((allSKUs, order) => {
    order.cartItems.forEach((item) => {
      allSKUs.add(item.sku);
    });
    return allSKUs;
  }, new Set());

  return Array.from(allSKUsSet);
};

const getAllOrdersTotal = (orders) => {
  return Object.values(orders).reduce((total, order) => {
    return total + getOrderTotal(order.cartItems);
  }, 0);
};

const qualifyingIncentiveSpend = ({
  orders,
  orderKey,
  incentiveProductId,
  appliesTo,
  voucherTotalCents = 0,
}) => {
  const order = orders[orderKey];
  const total =
    appliesTo === 'cart_level'
      ? getCartTotal(orders) + voucherTotalCents
      : getOrderTotal(order?.cartItems || []);
  const ownItem = (order?.cartItems || []).find(
    (item) => item.isIncentive && item.productId === incentiveProductId,
  );
  return total - (ownItem ? cartLineTotal(ownItem) : 0);
};

const eligibleIncentivesForOrder = (
  cartItems,
  incentiveProducts,
  voucherTotalCents = 0,
  hasVouchers = false,
  ignoredProductIds = [],
) => {
  // Check for at least one non-incentive product in the cart (vouchers count as non-incentive)
  const hasNonIncentiveProduct =
    cartItems.some((item) => !item.isIncentive) ||
    hasVouchers ||
    voucherTotalCents > 0;

  // If no non-incentive product is found, return an empty array
  if (!hasNonIncentiveProduct) {
    return [];
  }

  // Voucher purchases are cart-level spend. They can make a voucher-only cart
  // non-empty, but they do not count toward a threshold on this one order.
  const eligibleIncentives = incentiveProducts.filter((incentiveProduct) => {
    if (ignoredProductIds.includes(incentiveProduct.id)) {
      return false;
    }

    // Check if order total is greater than incentive product's threshold
    if (
      qualifyingIncentiveSpend({
        orders: { order: { cartItems } },
        orderKey: 'order',
        incentiveProductId: incentiveProduct.id,
        appliesTo: 'order_level',
      }) >= incentiveProduct.incentive_threshold &&
      incentiveProduct.incentive_applies_to !== 'cart_level'
    ) {
      // Check if incentive product is not yet in the user's cart
      const isInCart = cartItems.some(
        (cartItem) => cartItem.productId === incentiveProduct.id,
      );
      return !isInCart;
    }
    return false;
  });

  return eligibleIncentives;
};

// Cart-level identity is the benefit's SKU, not a product id: the same
// benefit (free shipping) is a separate row on every price list, so a
// decline recorded against one list's row must suppress every other list's
// row for the same benefit (ENG-2043 R3). Rows served without a sku fall
// back to their product id.
const cartLevelIgnoreKey = (product) =>
  product?.sku != null ? product.sku : product?.id;

// Whether an add-to-cart satisfies the pending re-add marker
// (`v3Order.optionalIncentiveToConfigure`). Product id is the normal match;
// a CART-LEVEL bonus also matches by sku, because the row the target order's
// own price list offers is a different row for the same benefit than the one
// the decline was recorded against (ENG-2043 R3/R7).
const optionalIncentiveMarkerMatchesProduct = (
  marker,
  { productId, sku, incentiveAppliesTo } = {},
) => {
  if (!marker) return false;
  if (marker.productId != null && marker.productId === productId) return true;

  return (
    incentiveAppliesTo === 'cart_level' &&
    marker.sku != null &&
    sku != null &&
    marker.sku === sku
  );
};

const eligibleIncentivesForCart = (
  orders,
  incentiveProducts,
  voucherTotalCents = 0,
  ignoredSkus = [],
) => {
  // Prices, thresholds, and voucherSubtotalCents all use cents.
  const eligibleIncentives = incentiveProducts.filter((incentiveProduct) => {
    if (ignoredSkus.includes(cartLevelIgnoreKey(incentiveProduct))) {
      return false;
    }

    // Check if order total is greater than incentive product's threshold
    if (
      qualifyingIncentiveSpend({
        orders,
        orderKey: Object.keys(orders).find((key) =>
          (orders[key].cartItems || []).some(
            (item) =>
              item.isIncentive && item.productId === incentiveProduct.id,
          ),
        ),
        incentiveProductId: incentiveProduct.id,
        appliesTo: 'cart_level',
        voucherTotalCents: voucherTotalCents || 0,
      }) >= incentiveProduct.incentive_threshold &&
      incentiveProduct.incentive_applies_to === 'cart_level'
    ) {
      // Check if incentive product is not yet in any order's cartItems.
      // For cart-level items the match is by SKU, not product id: the same
      // benefit (e.g. free shipping) is modelled as a separate product row
      // on each price list, and one copy in the cart satisfies all of them
      // (ENG-2965). The product-id check is kept for cart items persisted
      // before `sku` was reliably set.
      let isInCart = false;
      for (let orderId in orders) {
        if (
          orders[orderId].cartItems.some(
            (cartItem) =>
              cartItem.productId === incentiveProduct.id ||
              (cartItem.sku != null && cartItem.sku === incentiveProduct.sku),
          )
        ) {
          isInCart = true;
          break;
        }
      }
      return !isInCart;
    }
    return false;
  });

  return eligibleIncentives;
};

// The incentive catalog an order must be judged against (ENG-2043 R2). An
// order carries its own shoot's rows once it has been added to (or refreshed)
// under this code; the active catalog is only a fallback for an order that
// belongs to the active event, and an order from another shoot with no stash
// contributes nothing. One order's bonuses are never evaluated against
// another order's price list.
const incentiveCatalogForOrder = (
  order,
  activeCatalog,
  { isCurrentOrder = false } = {},
) => {
  if (Array.isArray(order?.orderLevelIncentiveProducts)) {
    return [
      ...order.orderLevelIncentiveProducts,
      ...(order.incentiveProducts || []),
    ];
  }

  // ENG-2043 R2: the active catalog stands in for a missing stash ONLY for the
  // parent's current order. Matching on the event id alone is not enough —
  // one event can hold several students' orders, and the gallery (price list)
  // can differ between them, so a sibling order on the same event would be
  // judged against a catalog that is not its own. The caller resolves the
  // current order key with the gallery check (see selectCurrentOrderKey), and
  // DP2 shoots, whose `event.id` is null, are covered by that same answer.
  if (isCurrentOrder) {
    return activeCatalog || [];
  }

  return [];
};

// The international rule applied per row, from each row's own
// `block_international_shipping` flag, so a stashed catalog keeps the
// restriction of the shoot that served it rather than the active shoot's.
const restrictionFilteredCatalog = (catalog, parentCountry = 'US') => {
  if (!Array.isArray(catalog)) return [];
  if (!parentCountry || parentCountry === 'US') return catalog;

  return catalog.filter(
    (product) =>
      !(
        product?.block_international_shipping === true &&
        (product?.requires_shipping === true ||
          product?.is_free_shipping === true)
      ),
  );
};

const orderHasPaidItem = (order) =>
  Array.isArray(order?.cartItems) &&
  order.cartItems.some((item) => !item.isIncentive);

// Optionality is a property of the order that earned the bonus, never of the
// last-opened gallery (ENG-2043 R1). An order added or refreshed under this
// code answers for itself; one persisted before the field existed is treated
// as opt-in ONLY when it is the parent's current order and the active shoot's
// company is opt-in — otherwise it may be a mandatory bonus, and Remove /
// "No thanks" must never be offered on one.
const orderIsOptIn = (
  order,
  { isCurrentOrder = false, activeOptionalIncentivesEnabled = false } = {},
) => {
  if (typeof order?.optionalIncentivesEnabled === 'boolean') {
    return order.optionalIncentivesEnabled;
  }

  return isCurrentOrder === true && activeOptionalIncentivesEnabled === true;
};

// Every order in the cart whose own stashed catalog offers this cart-level
// SKU. Used by the shared-SKU rule below.
const cartLevelSkuOwners = (orders, sku) =>
  Object.entries(orders || {})
    .filter(([, order]) =>
      (order?.incentiveProducts || []).some(
        (product) =>
          product &&
          product.incentive_applies_to === 'cart_level' &&
          cartLevelIgnoreKey(product) === sku,
      ),
    )
    .map(([orderKey]) => orderKey);

// Shared-SKU safety rule (ENG-2043 R3): a declined cart-level SKU may only be
// suppressed — and "No thanks" only offered — when EVERY order in the cart
// whose list offers that SKU is opt-in. If any mandatory list offers the same
// benefit the offer stands, or the submit gate would 422 `missing_incentives`
// for that company. With no stash to judge (legacy carts) the current order's
// flag is the fallback.
const cartLevelSkuIsDeclinable = (
  orders,
  sku,
  orderOptInMap = {},
  fallbackOptIn = false,
) => {
  const owners = cartLevelSkuOwners(orders, sku);

  if (owners.length === 0) {
    return fallbackOptIn === true;
  }

  return owners.every((orderKey) => orderOptInMap[orderKey] === true);
};

// The order a cart-level bonus belongs to: the first order with a paid item
// whose price list awarded it, else the first order whose own stash offers
// that row (phase-based lists resolve `priceList.id` from a different model
// than the incentive row's `company_pricing_model_id`). Both guarantee the
// same company, which `validate_selected_cart_incentive_owners!` requires.
// There is deliberately no "active order" fallback.
const ownerOrderForCartIncentive = (orders, incentiveProduct) => {
  const entries = Object.entries(orders || {}).filter(([, order]) =>
    orderHasPaidItem(order),
  );
  const companyPricingModelId = incentiveProduct?.company_pricing_model_id;

  if (companyPricingModelId !== null && companyPricingModelId !== undefined) {
    const byPriceList = entries.find(
      ([, order]) => order?.priceList?.id === companyPricingModelId,
    );

    if (byPriceList) {
      return { orderKey: byPriceList[0], order: byPriceList[1] };
    }
  }

  const byStash = entries.find(([, order]) =>
    (order?.incentiveProducts || []).some(
      (product) => product && product.id === incentiveProduct?.id,
    ),
  );

  return byStash ? { orderKey: byStash[0], order: byStash[1] } : null;
};

const eligibleOptionalCartLevelIncentives = ({
  orders,
  incentiveProducts,
  optionalCartLevelIncentiveSkus = [],
  voucherTotalCents = 0,
}) => {
  if (optionalCartLevelIncentiveSkus.length === 0) {
    return [];
  }

  return eligibleIncentivesForCart(
    orders,
    incentiveProducts,
    voucherTotalCents,
  ).filter((product) =>
    optionalCartLevelIncentiveSkus.includes(cartLevelIgnoreKey(product)),
  );
};

const unresolvedOptionalCartLevelIncentives = ({
  orders,
  incentiveProducts,
  optionalCartLevelIncentiveSkus = [],
  voucherTotalCents = 0,
}) => {
  return eligibleOptionalCartLevelIncentives({
    orders,
    incentiveProducts,
    optionalCartLevelIncentiveSkus,
    voucherTotalCents,
  }).flatMap((product) => {
    if (ownerOrderForCartIncentive(orders, product)) {
      return [];
    }

    return [
      {
        product,
        reason:
          product.company_pricing_model_id === null ||
          product.company_pricing_model_id === undefined
            ? 'missing_price_list_id'
            : 'no_qualifying_order',
      },
    ];
  });
};

// Everything the cart's "Available Bonuses" section lists (ENG-2043 R4):
// declined-but-still-earned bonuses for every OPT-IN order, each evaluated
// against its OWN catalog, plus cart-level benefits once per SKU from the
// listing union, routed to the order that earned them.
const availableOptionalIncentives = ({
  orders,
  activeIncentiveProducts = [],
  currentOrderKey = null,
  activeOptionalIncentivesEnabled = false,
  parentCountry = 'US',
  ignoresByOrder = {},
  ignoredCartLevelSkus = [],
  voucherTotalCents = 0,
  hasVouchers = false,
}) => {
  const orderEntries = Object.entries(orders || {});
  const optInByOrderKey = {};

  orderEntries.forEach(([orderKey, order]) => {
    optInByOrderKey[orderKey] = orderIsOptIn(order, {
      isCurrentOrder: orderKey === currentOrderKey,
      activeOptionalIncentivesEnabled,
    });
  });

  const orderLevelIncentives = orderEntries.flatMap(([orderKey, order]) => {
    if (!optInByOrderKey[orderKey]) {
      return [];
    }

    const ignoredProductIds = ignoresByOrder[orderKey] || [];

    if (ignoredProductIds.length === 0) {
      return [];
    }

    const catalog = restrictionFilteredCatalog(
      incentiveCatalogForOrder(order, activeIncentiveProducts, {
        isCurrentOrder: orderKey === currentOrderKey,
      }),
      parentCountry,
    );

    return eligibleIncentivesForOrder(
      order.cartItems || [],
      catalog,
      voucherTotalCents,
      hasVouchers,
    )
      .filter((product) => ignoredProductIds.includes(product.id))
      .map((product) => ({
        order,
        orderKey,
        product,
        scope: 'order_level',
      }));
  });

  // Cart-level bonuses are listed once per SKU off the listing union (every
  // list in the cart, configurable rows included) and must be configured
  // through the order whose list awarded them.
  const cartLevelIncentives = eligibleOptionalCartLevelIncentives({
    orders,
    incentiveProducts: unionCartLevelIncentiveProducts(
      orders,
      activeIncentiveProducts,
      parentCountry,
      { includeConfigurable: true },
    ),
    optionalCartLevelIncentiveSkus: ignoredCartLevelSkus,
    voucherTotalCents,
  }).flatMap((product) => {
    const declinable = cartLevelSkuIsDeclinable(
      orders,
      cartLevelIgnoreKey(product),
      optInByOrderKey,
      currentOrderKey ? optInByOrderKey[currentOrderKey] : false,
    );

    if (!declinable) {
      return [];
    }

    const owner = ownerOrderForCartIncentive(orders, product);

    return owner && optInByOrderKey[owner.orderKey]
      ? [
          {
            ...owner,
            product,
            scope: 'cart_level',
          },
        ]
      : [];
  });

  return [...orderLevelIncentives, ...cartLevelIncentives];
};

// Whether adding this bonus needs the product configurator at all: a pose
// slot, a background choice, a required product field or a required
// personalization value. A free-shipping row needs none of them.
const productRequiresIncentiveConfiguration = (product) =>
  productPosesCount(product) > 0 ||
  backgroundSlotsCount(product) > 0 ||
  (product?.required_product_fields || []).length > 0 ||
  product?.personalization_status === 'personalization_required';

// Re-qualify every incentive row after an edit that can LOWER an order's
// spend — removing a line item, or (once ENG-2967's cart steppers land)
// stepping a line's quantity down. Both paths must apply the identical
// policy, so it lives here rather than inline in the reducer cases.
//
// Takes the orders map with the edited order's cartItems ALREADY updated,
// the edited order's key, and the voucher subtotal in cents; returns a new
// orders map with unearned incentives dropped. Orders left holding nothing
// — or nothing but incentives — are removed entirely: a cart of free items
// alone is not an order.
//
// The rules (ENG-2965):
//
// getOrderTotal is the same formula every other qualification check uses
// (quantity, product options, BYO children) — a raw `item.price` sum
// under-counts and strips incentives the parent still earns. Cart-level
// incentives qualify off the whole cart, not one order; items persisted
// before `incentiveAppliesTo` existed fall back to the order-level rule,
// matching the old behavior.
//
// Voucher value counts toward qualification on the offer path
// (`eligibleIncentivesForCart` adds the voucher subtotal, in cents), so
// it has to count here too or an edit would strip an incentive the
// parent still earns. Order-level rows are judged without it, exactly
// as `eligibleIncentivesForOrder` does. Vouchers live in the
// cartVoucherItems slice, which the v3Order reducer cannot read;
// postConfigRoutingMiddleware injects the subtotal into the action
// payload. Absent (e.g. a direct dispatch in a test) it is 0.
//
// Incentive rows are counted, not excluded. Both other qualification
// paths already count them — the offer gates via
// `getOrderTotal(cartItems)` / `getCartTotal(orders)` over every item,
// and the submit gate in both of its bases with no incentive branch: it
// records on the catalog total (`retail_price * quantity`) and demands on
// the LOWER of that and the submitted total (`cart_item['price'] *
// quantity`), so a bonus is never required on evidence the client did not
// have. Incentives are normally `retail_price: 0`, so this
// is a no-op; where one is misconfigured with a price the parent is
// actually charged it (see docs/incentives.md), and excluding it here
// was the one place that could strip a benefit the offer path and the
// server both still consider earned.
// A cart-level incentive is earned from a price list, and the parent
// only holds it while that price list is still represented in the cart.
// Deleting the last item from the school whose list offered the benefit
// strands the row in a sibling order: the threshold can still be met by
// the other school's spend, but nothing in the cart offers the benefit
// any more. Nothing downstream catches that — the submit gate does not
// demand it (it is not earned per-list) and never rejects extras
// (ENG-2951), while CalculateTotalsPerOrder still zeroes shipping for
// any cart_level `is_free_shipping` row, so the studio eats a shipping
// charge the parent did not earn.
//
// `order.incentiveProducts` is that shoot's stashed cart-level catalog.
// An order created or refreshed under this code always carries an array
// (empty when its own list offers none), while one persisted before the
// field existed carries `undefined` — no information. The rule is only
// applied when every surviving order can answer; otherwise the
// threshold-only behaviour stands, so a stale cart is never stripped on
// missing evidence.
const cartLevelSkusOfferedByOrders = (orders) => {
  const offered = new Set();
  Object.values(orders).forEach((order) => {
    (order.incentiveProducts || []).forEach((product) => {
      if (product && product.incentive_applies_to === 'cart_level') {
        offered.add(product.sku);
      }
    });
  });
  return offered;
};

const everyOrderKnowsItsCatalog = (orders) =>
  Object.values(orders).every((order) =>
    Array.isArray(order.incentiveProducts),
  );

const ordersWithUnearnedIncentivesRemoved = (
  orders,
  editedOrderKey,
  voucherTotalCents = 0,
) => {
  const editedOrder = orders[editedOrderKey];

  if (!editedOrder) {
    return orders;
  }

  const editedCartItems = editedOrder.cartItems || [];
  // Mirrors the offer path exactly (see eligibleIncentivesFor{Order,Cart}):
  // voucher purchases are cart-level spend counted in cents, and never
  // count toward an order-level threshold. Re-qualification has to use
  // the same basis as the offer that granted the row, or an edit strips a
  // benefit the offer path still considers earned.
  const voucherTotal = voucherTotalCents || 0;

  // ...but an incentive never pays for its own threshold. The totals
  // above include every cart item, which is what the offer gates and
  // the submit gate both do, so a *different* priced row still counts.
  // The row being judged is the exception: at offer time it is not in
  // the cart yet, so the total that qualified it never contained its
  // price, and leaving it in here would let a misconfigured priced
  // incentive keep itself alive after ordinary spend drops below the
  // threshold. `getOrderTotal([item])` is exactly the contribution
  // that item made to the sums above, so subtracting it is exact.
  const qualifyingTotalFor = (item, orderKey, appliesTo) =>
    qualifyingIncentiveSpend({
      orders,
      orderKey,
      incentiveProductId: item.productId,
      appliesTo,
      voucherTotalCents: voucherTotal,
    });

  const isCartLevelIncentive = (item) =>
    item.isIncentive && item.incentiveAppliesTo === 'cart_level';

  let filteredIncentiveCartItems = editedCartItems.filter((item) => {
    if (!item.isIncentive) {
      return true;
    }
    const qualifyingTotal = qualifyingTotalFor(
      item,
      editedOrderKey,
      isCartLevelIncentive(item) ? 'cart_level' : 'order_level',
    );
    return item.incentiveThreshold <= qualifyingTotal;
  });

  const allIncentiveItems = filteredIncentiveCartItems.every(
    (item) => item.isIncentive,
  );

  // If all remaining items are incentive items, empty the order entirely.
  if (allIncentiveItems) {
    filteredIncentiveCartItems = [];
  }

  const newOrders = {
    ...orders,
    [editedOrderKey]: {
      ...editedOrder,
      cartItemIds: filteredIncentiveCartItems.map((item) => item.id),
      cartItems: filteredIncentiveCartItems,
    },
  };

  // If cartItems is empty, delete the order
  if (filteredIncentiveCartItems.length === 0) {
    delete newOrders[editedOrderKey];
  }

  // A cart-level incentive is added to whichever order was active when
  // the parent accepted it — not necessarily the order being edited
  // (see docs/incentives.md). So once the whole cart drops below its
  // threshold the benefit has to be pulled from sibling orders too,
  // or the parent keeps an unearned free-shipping row the submit gate
  // will happily accept (ENG-2951).
  //
  // The sibling test deliberately ignores `incentiveAppliesTo`: the
  // cart total is an upper bound on every order's own total, so a
  // threshold above it cannot be met under EITHER scope. That makes
  // the sweep correct without classifying the row, which matters for
  // items persisted before `incentiveAppliesTo` existed — they carry
  // no scope at all. Sibling incentives that still fit under the cart
  // total are left alone: this edit did not touch their order.
  Object.keys(newOrders).forEach((siblingKey) => {
    if (siblingKey === editedOrderKey) {
      return;
    }

    const siblingOrder = newOrders[siblingKey];
    const siblingCartItems = siblingOrder.cartItems || [];
    const qualifyingSiblingItems = siblingCartItems.filter(
      (item) =>
        !item.isIncentive ||
        item.incentiveThreshold <=
          qualifyingTotalFor(item, siblingKey, 'cart_level'),
    );

    if (qualifyingSiblingItems.length === siblingCartItems.length) {
      return;
    }

    if (qualifyingSiblingItems.length === 0) {
      delete newOrders[siblingKey];
      return;
    }

    newOrders[siblingKey] = {
      ...siblingOrder,
      cartItemIds: qualifyingSiblingItems.map((item) => item.id),
      cartItems: qualifyingSiblingItems,
    };
  });

  // Finally drop cart-level rows that no surviving price list offers
  // (see cartLevelSkusOfferedByOrders above). Order-level rows are left
  // alone: they belong to the order they sit in, and the stash only
  // describes cart-level catalogs.
  if (everyOrderKnowsItsCatalog(newOrders)) {
    const offeredSkus = cartLevelSkusOfferedByOrders(newOrders);

    Object.keys(newOrders).forEach((key) => {
      const order = newOrders[key];
      const items = order.cartItems || [];
      const stillOffered = items.filter(
        (item) => !isCartLevelIncentive(item) || offeredSkus.has(item.sku),
      );

      if (stillOffered.length === items.length) {
        return;
      }

      if (
        stillOffered.length === 0 ||
        stillOffered.every((i) => i.isIncentive)
      ) {
        delete newOrders[key];
        return;
      }

      newOrders[key] = {
        ...order,
        cartItemIds: stillOffered.map((item) => item.id),
        cartItems: stillOffered,
      };
    });
  }

  return newOrders;
};

// ENG-2965: `state.shoot.incentiveProducts` only ever holds the most
// recently opened shoot's catalog, but a cart-level incentive can live on
// the price list of any order in the cart. Each order stashes its own
// shoot's cart-level incentive catalog at add-to-cart time
// (`order.incentiveProducts`); this returns the active catalog's
// cart-level products plus the stashed ones, deduped by SKU keeping the
// lowest threshold. Stashed entries from a non-active catalog are only
// surfaced when they are free-shipping benefits — anything needing
// configuration (images, backgrounds) must be configured in its own
// shoot's context.
// `includeConfigurable` selects the LISTING union (everything every list in
// the cart offers, used by the cart's Available Bonuses / restricted-shipping
// cleanup) over the default OFFER union, which surfaces stashed entries only
// when they are free-shipping benefits — anything needing configuration must
// be configured in its own shoot's context.
const unionCartLevelIncentiveProducts = (
  orders,
  activeIncentiveProducts,
  parentCountry = 'US',
  { includeConfigurable = false } = {},
) => {
  const bySku = new Map();

  const add = (product) => {
    if (!product || product.incentive_applies_to !== 'cart_level') {
      return;
    }
    const existing = bySku.get(product.sku);
    if (
      existing &&
      (existing.incentive_threshold || 0) <= (product.incentive_threshold || 0)
    ) {
      return;
    }
    bySku.set(product.sku, product);
  };

  (activeIncentiveProducts || []).forEach(add);

  Object.values(orders || {}).forEach((order) => {
    (order.incentiveProducts || [])
      .filter(
        (product) => includeConfigurable || product.is_free_shipping === true,
      )
      // Stashed entries carry their own shoot's restriction flag so the
      // international rule survives catalog refreshes: a non-US parent on
      // a blocked shoot must not be offered free shipping from the stash.
      .filter(
        (product) =>
          !(
            product.block_international_shipping === true &&
            parentCountry !== 'US'
          ),
      )
      .forEach(add);
  });

  return Array.from(bySku.values());
};

// Sort cart items so that incentives come after non-incentives
const sortCartItems = (cartItems) => {
  return [...cartItems].sort((a, b) => {
    if (a.isIncentive === b.isIncentive) {
      // if both items are the same type (either both incentive or both non-incentive), don't change order
      return 0;
    }
    // if a is incentive and b is not, a should come after b
    if (a.isIncentive && !b.isIncentive) {
      return 1;
    }
    // if b is incentive and a is not, a should come before b
    if (!a.isIncentive && b.isIncentive) {
      return -1;
    }
  });
};

const cartItemProductHasContentsArray = (cartItem) => {
  return (
    cartItem.contents &&
    Array.isArray(cartItem.contents) &&
    cartItem.contents.length > 0
  );
};

const interstitialProductsToPrompt = (
  interstitialProducts,
  interstitialProductsPrompted,
  cartItems = [],
) => {
  const productsToPrompt = interstitialProducts.filter((product) => {
    if (
      product.lock_type !== 'unlockable' ||
      interstitialProductsPrompted.includes(product.id)
    )
      return false;

    return cartItems.some(
      (cartItem) =>
        cartItem.lockType === 'unlocker' &&
        product.lock_group.some((group) => cartItem.lockGroup.includes(group)),
    );
  });

  return productsToPrompt;
};

const nthFreeQualifiersInCart = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  return nthFreeQualifiersInCart;
};

const nthFreeProductsInCart = (cartItems) => {
  const nthFreeProductsInCart = cartItems.filter((item) => {
    return item.isNthFreeProduct === true;
  });

  return nthFreeProductsInCart;
};

const highestPriceNthFreeQualifier = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  if (nthFreeQualifiersInCart.length === 0) {
    return 0;
  }

  return nthFreeQualifiersInCart.reduce((highestPrice, currentItem) => {
    return Math.max(highestPrice, currentItem.price);
  }, nthFreeQualifiersInCart[0].price);
};

const lowestPriceNthFreeQualifier = (cartItems) => {
  const nthFreeQualifiersInCart = cartItems.filter((item) => {
    return item.isNthFreeQualifier === true;
  });

  if (nthFreeQualifiersInCart.length === 0) {
    return 0;
  }

  return nthFreeQualifiersInCart.reduce((lowestPrice, currentItem) => {
    return Math.min(lowestPrice, currentItem.price);
  }, nthFreeQualifiersInCart[0].price);
};

// Stamp each pose with the record type of the product slot it fills, so a
// persisted cart knows which photos are group photos without a lookup. A pose
// with no matching slot is an individual photo.
const stampImageRecordTypes = (poses, recordTypeAt) =>
  (poses || []).map((pose, index) =>
    pose ? { ...pose, imageRecordType: recordTypeAt(index) } : pose,
  );

const slotRecordType = (product, index) =>
  getImageRecords(product)[index]?.type || 'individual';

const optionValueMatches = (option, selectedValue) =>
  option.key === selectedValue || String(option.id) === String(selectedValue);

// The cart-line fields for a product's chosen options: selectedProductOptions
// for option sets (keyed by set, as productConfig and the retouching picker
// hold them), or a legacy single productOptionKey. Empty when nothing is
// chosen, so a caller can spread it over a line.
const productOptionCartFields = (
  productData,
  productOptions,
  productOptionKey,
) => {
  const fields = {};
  if (productOptions && Object.keys(productOptions).length > 0) {
    // New structure - multiple option sets
    const selectedOptions = [];

    // Process each option set selection
    Object.entries(productOptions).forEach(([optionSetKey, optionValue]) => {
      if (optionValue) {
        // Find the option set and selected option
        const optionSet = productData.product_option_sets?.find(
          (set) => set.key === optionSetKey,
        );

        if (optionSet) {
          const selectedOption = optionSet.product_options?.find((opt) =>
            optionValueMatches(opt, optionValue),
          );

          if (selectedOption) {
            selectedOptions.push({
              optionSetId: optionSet.id,
              optionSetKey: optionSet.key,
              optionSetName: optionSet.name,
              optionSetDisplayName: optionSet.display_name,
              optionId: selectedOption.id,
              optionKey: selectedOption.key,
              optionName: selectedOption.name,
              optionSku: selectedOption.sku,
              optionPrice: selectedOption.price || 0,
            });
          }
        }
      }
    });

    fields.selectedProductOptions = selectedOptions;

    // For backward compatibility, set the first option as productOption
    if (selectedOptions.length > 0) {
      fields.productOption = {
        id: selectedOptions[0].optionId,
        key: selectedOptions[0].optionKey,
        name: selectedOptions[0].optionName,
        sku: selectedOptions[0].optionSku,
        price: selectedOptions[0].optionPrice,
      };
    }
  } else if (productOptionKey) {
    // Legacy support - single option key
    const selectedOption = productData.product_options?.find(
      (option) => option.key === productOptionKey,
    );

    if (selectedOption) {
      fields.productOption = selectedOption;
      fields.selectedProductOptions = [
        {
          optionSetId: 'legacy',
          optionSetKey: 'legacy',
          optionSetName: 'Product Options',
          optionId: selectedOption.id,
          optionKey: selectedOption.key,
          optionName: selectedOption.name,
          optionSku: selectedOption.sku,
          optionPrice: selectedOption.price || 0,
        },
      ];
    }
  }

  return fields;
};

const configureCartItem = ({
  backgroundIds,
  backgroundSelections,
  incentiveProductIds,
  productConfig,
  productData,
  poses,
  poseIds,
  personalizationValueForCart,
  personalizationSetToSameValue,
  quantity,
  requiredFields,
  legacyRequiredFields,
  productOptionKey,
  productOptions,
  eventId,
  selectedCropOptions,
  childCropSelections = [],
  blockInternationalShipping = false,
  blockInternationalShippingChild = false,
}) => {
  let cartObject = {};
  cartObject.additionalData = productData.additional_data || {};
  cartObject.backgroundOptionsCount = productData.background_options_count;
  cartObject.productId = productData.id;
  cartObject.productName = productData.name;
  cartObject.quantity = quantity || 1;
  cartObject.personalizationValue = personalizationValueForCart;
  cartObject.price = productData.price;
  cartObject.nthFreeAmountAllowed = productData.nth_free_amount_allowed;
  cartObject.nthFreeProductId = productData.nth_free_product_id;
  cartObject.nthFreeSKU = productData.nth_free_sku;
  cartObject.nthFreeThreshold = productData.nth_free_threshold;
  cartObject.nthFreeValue = productData.nth_free_value;
  cartObject.personalizationStatus = productData.personalization_status;
  cartObject.posesCount = productData.poses_count;
  cartObject.imageNodes = productData.image_nodes || [];
  cartObject.productType = productData.product_type;
  cartObject.contents = productData.contents;
  cartObject.imageThumbnail = productData.image_thumbnail;
  cartObject.isGroup = productIsAllGroup(productData);
  cartObject.hasGroupRecord = productHasGroupRecord(productData);
  cartObject.incentiveAppliesTo = productData.incentive_applies_to || null;
  cartObject.incentiveThreshold = productData.incentive_threshold || null;
  cartObject.incentiveAppliesTo = productData.incentive_applies_to || null;
  cartObject.includesRetouching = productData.includes_retouching;
  cartObject.isPersonalization = productData.is_personalization;
  cartObject.isPreCartOfferProduct = productData.is_pre_cart_offer || false;
  cartObject.isRetouching = productData.is_retouching;
  // Snapshot the schedule onto the line so the cart prices it the same way the
  // server does, without a second lookup against the product list.
  cartObject.retouchingTieredUnitPricesCents =
    productData.tiered_unit_prices_cents || [];
  cartObject.lockType = productData.lock_type;
  cartObject.lockGroup = productData.lock_group;
  // product_type is authoritative: a cart-level incentive surfaced from a
  // non-active catalog (ENG-2965 union) has an id the active shoot's
  // incentiveProductIds list doesn't contain, but it is still an incentive
  // and must be visible to removal re-qualification and shipping cleanup.
  cartObject.isIncentive =
    incentiveProductIds.includes(productData.id) ||
    productData.product_type === 'incentive_product';
  cartObject.backgroundIds = backgroundIds || [];
  cartObject.backgroundSelections = backgroundSelections || [];
  cartObject.requiresShipping = productData.requires_shipping;
  cartObject.blockInternationalShipping = blockInternationalShipping === true;
  // Child-level shipping snapshot for bundles whose parent has
  // requires_shipping=false. `requiresShipping` alone misses selected or
  // required child products that still ship.
  cartObject.blockInternationalShippingChild =
    blockInternationalShippingChild === true;
  cartObject.sku = productData.sku;
  cartObject.eventId = eventId;

  // TODO: update this to use poseIds as passed... need to confirm that YPX/BYO is submitting poseIds
  cartObject.poseIds = poses ? poses.map((p) => p.id) : poseIds || [];

  // A multi-config product's pose N fills child product N's single slot.
  cartObject.poses = stampImageRecordTypes(poses, (index) =>
    productData.is_multi_config
      ? slotRecordType(productData.child_products?.[index], 0)
      : slotRecordType(productData, index),
  );
  cartObject.productIds = [];
  cartObject.isBYO = productData.is_byo;
  cartObject.isYPX = productData.is_ypx;
  cartObject.isMultiConfig = productData.is_multi_config;
  cartObject.requiredFields = requiredFields;
  cartObject.legacyRequiredFields = legacyRequiredFields;
  const selectedCrop = poses?.find(
    (pose) => !!pose.selected_crop,
  )?.selected_crop;
  cartObject.selectedCrop = selectedCrop;
  cartObject.selectedCropOptions = selectedCropOptions || [];

  // establish whether product is an nth-free product
  cartObject.isNthFreeProduct = productData.product_type == 'nth_free';

  // Handle product options - support both legacy single option and new multiple options
  Object.assign(
    cartObject,
    productOptionCartFields(productData, productOptions, productOptionKey),
  );

  let isNthFreeQualifier = false;

  if (
    productData.nth_free_sku &&
    productData.nth_free_sku !== null &&
    productData.nth_free_sku !== ''
  ) {
    isNthFreeQualifier = true;
  }

  cartObject.isNthFreeQualifier = isNthFreeQualifier;

  if (personalizationValueForCart) {
    // do nothing
  } else {
    // get personalization value for child items from productConfig
    const personalizationValues = Object.values(productConfig).map(
      (p) => p.personalization,
    );
    personalizationValues.reverse();
    cartObject.personalizationValue = personalizationSetToSameValue
      ? personalizationValues.find((p) => !p)
      : null;
  }

  // !productData.is_personalization handles case where personalization producted is added to cart
  // alongside a personalized YPX product
  // TODO: find a better way to handle this
  if (!productData.is_personalization) {
    cartObject.children = Object.values(productConfig).map((child) => {
      const { product } = child;
      if (child.product) cartObject.productIds.push(child.product.id);
      if (child.background) {
        cartObject.backgroundIds.push(child.background.id);
        cartObject.backgroundSelections.push(child.background);
      }

      // Support both single .pose and multiple .poses per slot
      const childPoses = stampImageRecordTypes(
        Array.isArray(child.poses)
          ? child.poses
          : child.pose
          ? [child.pose]
          : [],
        (index) => slotRecordType(product, index),
      );
      childPoses.forEach((p) => cartObject.poseIds.push(p.id));

      // Process product options for this child
      let childSelectedOptions = [];
      if (
        child.productOptions &&
        Object.keys(child.productOptions).length > 0
      ) {
        Object.entries(child.productOptions).forEach(
          ([optionSetKey, optionValue]) => {
            if (optionValue) {
              // Find the option set and selected option from the child product
              const optionSet = product.product_option_sets?.find(
                (set) => set.key === optionSetKey,
              );
              if (optionSet) {
                const selectedOption = optionSet.product_options?.find((opt) =>
                  optionValueMatches(opt, optionValue),
                );
                if (selectedOption) {
                  childSelectedOptions.push({
                    optionSetId: optionSet.id,
                    optionSetKey: optionSet.key,
                    optionSetName: optionSet.display_name || optionSet.name,
                    optionId: selectedOption.id,
                    optionKey: selectedOption.key,
                    optionName: selectedOption.name,
                    optionSku: selectedOption.sku,
                    optionPrice: selectedOption.price || 0,
                  });
                }
              }
            }
          },
        );
      }

      return {
        price: product.price,
        productId: product.id,
        productName: product.name,
        backgrounds: child.background ? [child.background] : [],
        poses: childPoses,
        personalizationStatus: product.personalization_status,
        // A child the price list marks no_personalization must never carry a
        // personalization value to the order, whatever landed in productConfig.
        // The personalization step is reachable directly (its route has no
        // guard, so the back button lands on it), so the routing gate in
        // productConfig/utils.js cannot be the only check.
        personalizationValue:
          product.personalization_status === 'no_personalization'
            ? null
            : child.personalization,
        quantity: 1,
        additionalData: {},
        variantId: null,
        selectedProductOptions: childSelectedOptions,
        selectedCropOptions: Array.isArray(child.cropOptions)
          ? child.cropOptions
          : [],
      };
    });

    // TODO: revisit this logic -- it might be better to handle this via the above 'child' logic
    if (productData.is_multi_config) {
      cartObject.multiConfigChildProducts = productData.child_products.map(
        (child, index) => {
          if (child) cartObject.productIds.push(child.id);

          const pose = cartObject.poses[index];
          const background = cartObject.backgroundSelections[index];

          if (pose) {
            cartObject.poseIds.push(pose.id);
          }

          return {
            price: child.price,
            productId: child.id,
            productName: child.name,
            backgrounds: background ? [background] : [],
            poses: pose ? [pose] : [],
            // personalizationStatus: product.personalization_status, // TODO: do we need to handle personalization?
            // personalizationValue: product.personalization, // TODO: do we need to handle personalization?
            quantity: 1,
            additionalData: {},
            variantId: null,
            selectedCropOptions: childCropSelections[index] || [],
          };
        },
      );
    }
  }

  return cartObject;
};

const cartContainsPersonalizableProducts = (cartItems) => {
  return cartItems.some((cartItem) => {
    // Check the personalization status of the cart item itself
    const isPersonalizable =
      cartItem.personalizationStatus === 'personalization_required' ||
      cartItem.personalizationStatus === 'personalization_optional';

    // Check if there are children and if any of them have a required or optional personalization status
    const hasPersonalizableChildren =
      cartItem.children &&
      cartItem.children.some((childProduct) => {
        return (
          childProduct.personalizationStatus === 'personalization_optional' ||
          childProduct.personalizationStatus === 'personalization_required'
        );
      });

    // Return true if either the item itself or any of its children are personalizable
    return isPersonalizable || hasPersonalizableChildren;
  });
};

const cartContainsNonIncentiveProducts = (cartItems) => {
  return cartItems.some((cartItem) => !cartItem.isIncentive);
};

const determineCartItemPrice = (cartItem) => {
  let basePrice = 0;

  if (cartItem.isBYO) {
    const freeIndices = cartItem?.additionalData?.free_indices || [];

    // Check if cartItem has a 'children' property and that it's an array
    if (Array.isArray(cartItem.children)) {
      // Sum the price of each child, unless its index is in the freeIndices array
      basePrice = cartItem.children.reduce((acc, child, index) => {
        if (freeIndices.includes(index)) {
          return acc;
        }
        let childPrice = child.price || 0;
        // Add child's product option prices
        if (
          child.selectedProductOptions &&
          Array.isArray(child.selectedProductOptions)
        ) {
          const childOptionPrices = child.selectedProductOptions.reduce(
            (optTotal, option) => optTotal + (option.optionPrice || 0),
            0,
          );
          childPrice += childOptionPrices;
        }
        return acc + childPrice;
      }, 0);
    }
  } else if (cartItem.isYPX) {
    // For YPX products, use the parent price and add only the child option prices
    basePrice = cartItem.price || 0;

    // Add only the product option prices from children
    if (Array.isArray(cartItem.children)) {
      const childOptionPrices = cartItem.children.reduce((acc, child) => {
        if (
          child.selectedProductOptions &&
          Array.isArray(child.selectedProductOptions)
        ) {
          const optionTotal = child.selectedProductOptions.reduce(
            (optTotal, option) => optTotal + (option.optionPrice || 0),
            0,
          );
          return acc + optionTotal;
        }
        return acc;
      }, 0);
      basePrice += childOptionPrices;
    }
  } else {
    // Regular product base price
    basePrice = cartItem.price || 0;
  }

  // Add product option prices
  let optionPricesTotal = 0;
  if (
    cartItem.selectedProductOptions &&
    Array.isArray(cartItem.selectedProductOptions)
  ) {
    optionPricesTotal = cartItem.selectedProductOptions.reduce(
      (total, option) => {
        return total + (option.optionPrice || 0);
      },
      0,
    );
  } else if (cartItem.productOption && cartItem.productOption.price) {
    // Legacy single option support
    optionPricesTotal = cartItem.productOption.price;
  }

  return basePrice + optionPricesTotal;
};

const cartItemsIncludeRetouching = (cartItems) => {
  return cartItems?.some((cartItem) => cartItem?.includesRetouching === true);
};

const cartItemsContainRetouchingProduct = (cartItems) => {
  return cartItems?.some((cartItem) => cartItem?.isRetouching === true);
};

const applyDiscountCodeToCart = (currentState, discountCode) => {
  const discountCodes = currentState.discountCodes || [];

  if (
    discountCodes.some(
      (code) => code.discount_code_id === discountCode.discount_code_id,
    )
  ) {
    return currentState;
  }

  return {
    ...currentState,
    discountCodes: [...discountCodes, discountCode],
  };
};

const cartItemsAreGroupProductsOnly = (cartItems) => {
  return cartItems.every((cartItem) => cartItem.isGroup === true);
};

const cartContainsPrepayOrders = (orders) => {
  const isPrepay = Object.values(orders).some((order) => {
    return order.isPrepay === true;
  });

  return isPrepay;
};

const cartContainsPostEventOrders = (orders) => {
  const isPostEvent = Object.values(orders).some((order) => {
    return order.isPrepay === false;
  });

  return isPostEvent;
};

const cartContainsShippingRequiredProducts = (orders) => {
  const hasShippingRequiredCartItems = Object.values(orders).some((order) => {
    return order.cartItems.some((item) => item?.requiresShipping === true);
  });

  return hasShippingRequiredCartItems;
};

// Whether a cart item ships from the parent's region — the cart-time
// mirror of isProductAllowed's leaf/YPX/multi-config branching:
//
//   - Leaf: parent's own requiresShipping is authoritative.
//   - YPX (non-multi-config parent): children decide. The cart item's
//     blockInternationalShippingChild reflects the selected children's
//     ship-flag (set at add-to-cart time); the parent's coarse
//     requiresShipping is often true even when an all-digital selection
//     was made and so must be ignored.
//   - Multi-config: children are required and not substitutable, so EITHER
//     the parent's requires_shipping OR any child's must block (matches
//     isProductAllowed's two-clause check).
const cartItemShipsForRestriction = (item) => {
  if (!item) return false;
  const isMultiConfig = item.isMultiConfig === true;
  const isYpxParent =
    (item.productType === 'parent_product' || item.isYPX === true) &&
    !isMultiConfig;

  if (isMultiConfig) {
    return (
      item.requiresShipping === true ||
      item.blockInternationalShippingChild === true
    );
  }
  if (isYpxParent) {
    return item.blockInternationalShippingChild === true;
  }
  return item.requiresShipping === true;
};

const internationalShippingViolations = (orders, country) => {
  if (!country || country === 'US') return [];
  return Object.values(orders || {}).flatMap((order) =>
    (order?.cartItems || []).filter(
      (item) =>
        item?.blockInternationalShipping === true &&
        cartItemShipsForRestriction(item),
    ),
  );
};

// Returns [{ orderKey, cartItemId }] for incentive cart items the parent
// can't use. Two cases:
//   - Shippable incentives: same as cartItemShipsForRestriction — YPX-aware,
//     scoped to isIncentive=true items only.
//   - Free-shipping incentives: identified by matching productId against the
//     incentive product list (the "Free Shipping" product is typically
//     modeled with requires_shipping=false, so we can't rely on that flag).
//     `incentiveProducts` is optional — when omitted, only the first case
//     is detected. Used by Cart.jsx to auto-remove these on cart mount.
const removableShippingIncentives = (orders, country, incentiveProducts) => {
  if (!country || country === 'US') return [];
  const freeShippingProductIds = new Set(
    (Array.isArray(incentiveProducts) ? incentiveProducts : [])
      .filter((p) => p?.is_free_shipping === true)
      .map((p) => p?.id),
  );
  return Object.entries(orders || {}).flatMap(([orderKey, order]) =>
    (order?.cartItems || [])
      .filter(
        (item) =>
          item?.isIncentive === true &&
          item?.blockInternationalShipping === true &&
          (cartItemShipsForRestriction(item) ||
            freeShippingProductIds.has(item?.productId)),
      )
      .map((item) => ({ orderKey, cartItemId: item.id })),
  );
};

const hasLockedProductsNoUnlockers = (cartItems) => {
  // Check for undefined, null, or non-array input and return false
  if (!Array.isArray(cartItems)) {
    return false;
  }

  // Check if there is at least one item with lockType === 'unlockable'
  const hasUnlockable = cartItems.some(
    (item) => item && item.lockType === 'unlockable',
  );

  // Check if there are no items with lockType === 'unlocker'
  const hasNoUnlocker = cartItems.every(
    (item) => item && item.lockType !== 'unlocker',
  );

  // Return true if both conditions are met, otherwise return false
  return hasUnlockable && hasNoUnlocker;
};

const lockedProductIds = (cartItems) => {
  // Check for undefined, null, or non-array input and return an empty array
  if (!Array.isArray(cartItems)) {
    return [];
  }

  const lockedIds = cartItems
    .filter((item) => {
      return item && item.lockType === 'unlockable';
    })
    .map((item) => item.id);

  return lockedIds;
};

const preCartOfferProductIds = (cartItems) => {
  return cartItems
    .filter((item) => item.isPreCartOfferProduct)
    .map((item) => item.id);
};

const productIdsWithLockedBackgrounds = (cartItems, unlockedGroups) => {
  if (!Array.isArray(cartItems)) {
    return [];
  }

  const productIds = cartItems
    .filter(({ backgroundSelections }) =>
      backgroundSelections.some(
        ({ lock_type, lock_group }) =>
          lock_type === 'unlockable' &&
          !unlockedGroups.some((set) => lock_group.includes(set)),
      ),
    )
    .map((item) => item.id);

  return productIds;
};

const productBackgroundLockGroup = (cartItems) => {};

const preCartOfferProductsToPrompt = (
  cartObject,
  preCartOfferProducts,
  triggersPreCartOfferProducts,
) => {
  // Return empty array if any argument is falsy or not an array where expected
  if (
    !cartObject ||
    !Array.isArray(preCartOfferProducts) ||
    !Array.isArray(triggersPreCartOfferProducts) ||
    cartObject.cartItems?.every(
      (item) => item.isPersonalization || item.isRetouching,
    )
  ) {
    return [];
  }

  // Ensure cartItems is an array, then map over it, default to an empty array if cartItems is undefined/null
  // Look through 'children' to handle YPX/BYO products
  const cartProductIds = Array.isArray(cartObject.cartItems)
    ? cartObject.cartItems.flatMap((item) => {
        // Get the productId of the current item
        const productIds = [item.productId];

        // If the item has children, add their productIds to the array
        if (Array.isArray(item.children)) {
          item.children.forEach((child) => {
            if (child.productId) {
              productIds.push(child.productId);
            }
          });
        }

        return productIds;
      })
    : [];

  // Check if any of the triggersPreCartOfferProducts are in the cart
  const isTriggerProductInCart = triggersPreCartOfferProducts.some((product) =>
    cartProductIds.includes(product.id),
  );

  // Use empty array as a default value for preCartOfferProductsPrompted
  const preCartOfferProductsPromptedIds =
    cartObject.preCartOfferProductsPrompted || [];

  // If a trigger product is in the cart, return empty array
  if (isTriggerProductInCart) {
    return [];
  }

  // Filter preCartOfferProducts to include products not yet prompted and not in the cart
  return preCartOfferProducts.filter(
    (product) =>
      !cartProductIds.includes(product.id) &&
      !preCartOfferProductsPromptedIds.includes(product.id),
  );
};

const cartHasShippingCost = (cartTotalsObject) =>
  cartTotalsObject &&
  typeof cartTotalsObject.shipping === 'number' &&
  cartTotalsObject.shipping > 0;

// Determine whether the order contains a single background upgrade and return true if the studio has set the single_charge_for_background_upgrades flag
const orderContainsSingleBackgroundUpgrade = (
  orders,
  studentId,
  chargeForYpxBackgroundUpgrades = false,
) => {
  if (orders && orders[studentId]) {
    const cartItems = orders[studentId].cartItems;

    for (const cartItem of cartItems) {
      if (cartItem.backgroundSelections) {
        for (const selection of cartItem.backgroundSelections) {
          if (selection.price && parseInt(selection.price) > 0) {
            if (
              cartItem?.isYPX === true &&
              chargeForYpxBackgroundUpgrades === false
            ) {
              return false;
            } else {
              if (selection.single_charge_for_background_upgrades) {
                return true;
              }
            }
          }
        }
      }
    }
  }

  return false;
};

const anyOrderContainsOnlyRetouching = (orders) => {
  // Check if ANY order contains only retouching products
  for (let studentId in orders) {
    const order = orders[studentId];

    // If the order has no items, skip it
    if (!order.cartItems || order.cartItems.length === 0) {
      continue;
    }

    // Check if ALL items in this order are retouching
    const allItemsAreRetouching = order.cartItems.every(
      (cartItem) => cartItem.isRetouching === true,
    );

    // If we found an order with only retouching items, return true
    if (allItemsAreRetouching) {
      return true;
    }
  }

  // No orders contain only retouching products
  return false;
};

// Collect the poses attached to a set of non-retouching cart items —
// top-level poses, package child poses, and multi-config child poses —
// deduplicated by image id.
const isGroupPose = (pose) => pose?.imageRecordType === 'group';

// Ids of the group photos on an order's non-retouching lines. A photo is a
// group photo wherever it appears, so an id seen only in a line's poseIds is
// still excluded when another line carries its stamped pose object.
const nonRetouchingGroupPoseIds = (cartItems) => {
  const ids = new Set();
  const collect = (source) =>
    (source?.poses || []).forEach((pose) => {
      if (pose?.id != null && isGroupPose(pose)) ids.add(pose.id);
    });

  (cartItems || []).forEach((cartItem) => {
    if (cartItem.isRetouching) return;
    collect(cartItem);
    (cartItem.children || []).forEach(collect);
    (cartItem.multiConfigChildProducts || []).forEach(collect);
  });

  return ids;
};

const collectNonRetouchingPoses = (cartItems) => {
  const poseIds = [];
  const allowedIds = new Set();
  const posesById = new Map();
  const groupIds = nonRetouchingGroupPoseIds(cartItems);

  const allowId = (id) => {
    if (id == null || allowedIds.has(id) || groupIds.has(id)) return;
    allowedIds.add(id);
    poseIds.push(id);
  };
  const allowPose = (pose) => {
    if (!pose || pose.id == null || isGroupPose(pose)) return;
    allowId(pose.id);
    if (!posesById.has(pose.id)) posesById.set(pose.id, pose);
  };
  const collectSource = (source) => {
    (source?.poses || []).forEach(allowPose);
    (source?.poseIds || []).forEach(allowId);
  };

  (cartItems || []).forEach((cartItem) => {
    if (cartItem.isRetouching) return;

    collectSource(cartItem);
    (cartItem.children || []).forEach(collectSource);
    (cartItem.multiConfigChildProducts || []).forEach(collectSource);
  });

  // Persisted source lines can retain poseIds without the corresponding pose
  // objects. A retouching line contains the same selected pose payload, so use
  // it only as an object lookup for ids proven to belong to a non-retouch line.
  (cartItems || []).forEach((cartItem) => {
    if (!cartItem.isRetouching) return;
    (cartItem.poses || []).forEach((pose) => {
      if (
        pose?.id != null &&
        allowedIds.has(pose.id) &&
        !posesById.has(pose.id)
      ) {
        posesById.set(pose.id, pose);
      }
    });
  });

  return poseIds.map((id) => posesById.get(id)).filter(Boolean);
};

const collectNonRetouchingPoseIds = (cartItems) => {
  const ids = new Set();
  const collect = (source) => {
    (source?.poses || []).forEach((pose) => {
      if (pose?.id != null) ids.add(pose.id);
    });
    (source?.poseIds || []).forEach((id) => {
      if (id != null) ids.add(id);
    });
  };

  (cartItems || []).forEach((cartItem) => {
    if (cartItem.isRetouching) return;
    collect(cartItem);
    (cartItem.children || []).forEach(collect);
    (cartItem.multiConfigChildProducts || []).forEach(collect);
  });

  return ids;
};

// The distinct images a customer could select for retouching: every pose
// attached to the order's current cart items (excluding retouching items).
const canSelectRetouchingForOrder = ({ order, requiresImageSelection }) =>
  requiresImageSelection === true &&
  order?.isPrepay !== true &&
  distinctCartPoses(order).length > 0;

const distinctCartPoses = (order) => {
  return collectNonRetouchingPoses(order?.cartItems);
};

// An order whose photos are all group photos cannot buy retouching: it has a
// group photo on a non-retouching line and nothing left to select.
const orderHasOnlyGroupPhotos = (order) =>
  nonRetouchingGroupPoseIds(order?.cartItems).size > 0 &&
  distinctCartPoses(order).length === 0;

// The retouching line a photo is selected on, or null.
const retouchingLineForPose = (order, poseId) => {
  if (poseId == null) return null;
  const cartItem = (order?.cartItems || []).find(
    (item) =>
      item.isRetouching &&
      (item.poseIds || []).some((id) => String(id) === String(poseId)),
  );
  return cartItem ? { cartItem, productId: cartItem.productId } : null;
};

// Keep only the entries of a per-photo map (retouchingNotes,
// retouchingSources) whose photo is still selected; undefined when none are.
const narrowPerPhotoMap = (map, keptIds) => {
  if (!map || typeof map !== 'object') return undefined;
  const kept = new Set(keptIds.map(String));
  const entries = Object.entries(map).filter(([id]) => kept.has(id));
  return entries.length ? Object.fromEntries(entries) : undefined;
};

// After a cart item is removed, drop any retouching selections that
// referenced images no longer in the cart. Quantity tracks the remaining
// selection count; a retouching item pruned to zero selections is removed
// entirely. Retouching items with no explicit selections (legacy flag-off
// behavior) pass through untouched.
const pruneRetouchingSelections = (
  cartItems,
  requiresImageSelection = false,
) => {
  if (!requiresImageSelection) return cartItems || [];

  const allowedIds = collectNonRetouchingPoseIds(cartItems);

  return (cartItems || [])
    .map((cartItem) => {
      if (!cartItem.isRetouching) return cartItem;
      if (!(cartItem.poseIds || []).length) return cartItem;

      const keptPoses = (cartItem.poses || []).filter((pose) =>
        allowedIds.has(pose.id),
      );
      const keptIds = (cartItem.poseIds || []).filter((id) =>
        allowedIds.has(id),
      );

      if (keptIds.length === cartItem.poseIds.length) return cartItem;
      if (keptIds.length === 0) return null; // every selected image left the cart

      // Notes and sources are per photo: narrow them with the selection and
      // drop a map that ends up empty.
      const rest = { ...cartItem };
      delete rest.retouchingNotes;
      delete rest.retouchingSources;
      const retouchingNotes = narrowPerPhotoMap(
        cartItem.retouchingNotes,
        keptIds,
      );
      const retouchingSources = narrowPerPhotoMap(
        cartItem.retouchingSources,
        keptIds,
      );

      return {
        ...rest,
        ...(retouchingNotes ? { retouchingNotes } : {}),
        ...(retouchingSources ? { retouchingSources } : {}),
        poses: keptPoses,
        poseIds: keptIds,
        quantity: keptIds.length,
        // Re-derived from the surviving poses for the same reason the
        // V3_UPDATE_RETOUCHING_SELECTION reducer case does it: a line pruned
        // down to one image picks up the cart-level crop server-side.
        selectedCrop: keptPoses.find((pose) => !!pose.selected_crop)
          ?.selected_crop,
      };
    })
    .filter(Boolean);
};

export default {
  allOrdersAreEmpty,
  anyOrderContainsOnlyRetouching,
  applyDiscountCodeToCart,
  availableOptionalIncentives,
  calculateTotalPrice,
  cartLineTotal,
  marginalRetouchingPrices,
  marginalRetouchingPriceForNext,
  orderHasOnlyGroupPhotos,
  productOptionCartFields,
  retouchingImageCount,
  retouchingLineForPose,
  validRetouchingPrice,
  validRetouchingLinePrice,
  validRetouchingPoseIds,
  qualifyingIncentiveSpend,
  canSelectRetouchingForOrder,
  cartContainsNonIncentiveProducts,
  cartContainsPersonalizableProducts,
  cartContainsPostEventOrders,
  cartContainsPrepayOrders,
  cartContainsShippingRequiredProducts,
  internationalShippingViolations,
  removableShippingIncentives,
  cartHasShippingCost,
  cartItemsAreGroupProductsOnly,
  cartItemsContainRetouchingProduct,
  cartItemsIncludeRetouching,
  cartItemProductHasContentsArray,
  configureCartItem,
  createNewCartItem,
  createNewPersonalizationCartItem,
  determineCartItemPrice,
  distinctCartPoses,
  cartLevelSkuIsDeclinable,
  cartLevelSkuOwners,
  eligibleIncentivesForCart,
  eligibleIncentivesForOrder,
  eligibleOptionalCartLevelIncentives,
  incentiveCatalogForOrder,
  optionalIncentiveMarkerMatchesProduct,
  orderIsOptIn,
  productRequiresIncentiveConfiguration,
  restrictionFilteredCatalog,

  unionCartLevelIncentiveProducts,
  getAllCartSKUs,
  getAllOrdersTotal,
  getCount,
  getNextVariantId,
  getOrderTotal,
  hasLockedProductsNoUnlockers,
  highestPriceNthFreeQualifier,
  lowestPriceNthFreeQualifier,
  interstitialProductsToPrompt,
  lockedProductIds,
  nthFreeProductsInCart,
  nthFreeQualifiersInCart,
  ownerOrderForCartIncentive,
  orderHasNoRetouching,
  orderContainsSingleBackgroundUpgrade,
  ordersWithUnearnedIncentivesRemoved,
  preCartOfferProductsToPrompt,
  preCartOfferProductIds,
  pruneRetouchingSelections,
  productIdsWithLockedBackgrounds,
  sortCartItems,
  sumUniqueBackgroundPrices,
  unresolvedOptionalCartLevelIncentives,
  unlockedGroups,
};
